import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { GuestService } from '@core/auth/guest.service';
import { UserService } from '@core/services/user.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map, of, switchMap } from 'rxjs';
import { pickForwardedAuthParams } from './auth.config';
import { initGuestUser } from './guest.guard';

export const REDIRECT_URL_KEY = 'MIB_auth_redirect_url';

export const AuthGuard: CanActivateFn = (route, state) => {
  const oidcSecurityService = inject(OidcSecurityService);
  const userService = inject(UserService);
  const guestService = inject(GuestService);
  const router = inject(Router);

  const sharedIds = guestService.hasValidToken() ? guestService.getSharedIds() : [];
  const sharedId: string = route.params['id'];
  if (route.routeConfig?.path === 'video-details/:id' && sharedIds.includes(sharedId)) {
    initGuestUser(sharedId, userService, guestService);
    return of(true);
  }

  const resolvePostAuthTarget = (): boolean | UrlTree => {
    const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY);
    if (redirectUrl) {
      sessionStorage.removeItem(REDIRECT_URL_KEY);
      return router.parseUrl(redirectUrl);
    }
    return true;
  };

  // Auth0 returned an error callback (e.g. no connections enabled for the
  // organization). Avoid an infinite authorize→error→authorize loop by
  // surfacing the error instead of redirecting again.
  if (route.queryParams['error']) {
    const errorCode: string = route.queryParams['error'];
    const errorDesc: string = route.queryParams['error_description'] ?? '';
    console.error('[AuthGuard] Auth0 returned an error:', errorCode, errorDesc);
    return of(
      router.createUrlTree(['/auth-error'], {
        queryParams: { error: errorCode, error_description: errorDesc },
      }),
    );
  }

  return oidcSecurityService.checkAuth().pipe(
    switchMap((loginResponse) => {
      if (loginResponse.isAuthenticated) {
        const userData = loginResponse.userData;
        return !userService.userLoaded()
          ? userService.syncUserSession(userData).pipe(map(resolvePostAuthTarget))
          : of(resolvePostAuthTarget());
      }
      sessionStorage.setItem(REDIRECT_URL_KEY, state.url);
      oidcSecurityService.authorize(undefined, {
        customParams: pickForwardedAuthParams(route.queryParams),
      });
      return of(false);
    }),
  );
};
